import React, { useState, useEffect } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faCheckCircle, faBuilding } from '@fortawesome/free-solid-svg-icons';

interface BrokerageStatusCheckerProps {
  brokerageId: string;
  onSetupRequired?: () => void;
}

interface BrokerageStatus {
  exists: boolean;
  name?: string;
  error?: string;
}

const BrokerageStatusChecker: React.FC<BrokerageStatusCheckerProps> = ({ 
  brokerageId, 
  onSetupRequired 
}) => {
  const [status, setStatus] = useState<BrokerageStatus | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (brokerageId && brokerageId.trim() !== '') {
      checkBrokerageStatus();
    }
  }, [brokerageId]);

  const checkBrokerageStatus = async () => {
    // Skip brokerage validation for now - assume brokerage exists if ID is provided
    setLoading(false);
    
    if (brokerageId && brokerageId.trim() !== '') {
      setStatus({ 
        exists: true, 
        name: 'Your Brokerage' 
      });
    } else {
      setStatus({ 
        exists: false, 
        error: 'No brokerage ID provided' 
      });
    }
  };

  if (loading) {
    return (
      <Alert variant="info" className="d-flex align-items-center">
        <Spinner size="sm" className="me-2" />
        Verifying brokerage setup...
      </Alert>
    );
  }

  if (!status) {
    return null;
  }

  if (status.exists) {
    return (
      <Alert variant="success" className="d-flex align-items-center">
        <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
        <div>
          <strong>Brokerage Verified:</strong> {status.name}
          <br />
          <small className="text-muted">ID: {brokerageId}</small>
        </div>
      </Alert>
    );
  }

  return (
    <Alert variant="warning">
      <div className="d-flex align-items-start">
        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 mt-1" />
        <div className="flex-grow-1">
          <strong>Brokerage Setup Required</strong>
          <p className="mb-2">
            The brokerage (ID: {brokerageId}) was not found in the system. 
            You need to complete your brokerage profile setup before adding agents.
          </p>
          <p className="mb-2 text-muted">
            <strong>Error:</strong> {status.error}
          </p>
          {onSetupRequired && (
            <Button 
              variant="warning" 
              size="sm"
              onClick={onSetupRequired}
            >
              <FontAwesomeIcon icon={faBuilding} className="me-2" />
              Setup Brokerage Profile
            </Button>
          )}
        </div>
      </div>
    </Alert>
  );
};

export default BrokerageStatusChecker;